import { Icon } from "@circle-react-shared/Icon";

import { t } from "@/i18n-js/instance";
import { ConnectedBadge } from "@/react/components/ConnectionButtons/ConnectedBadge";
import { ConnectionButtonsOrchestrator } from "@/react/components/ConnectionButtons/ConnectionButtonsOrchestrator";
import { useConnectionButtonState } from "@/react/components/ConnectionButtons/hooks/useConnectionButtonState";
import { useIsMemberDirectoryMapAvailable } from "@/react/components/MemberDirectory/useIsMemberDirectoryMapAvailable";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useIsConnectPopoverPanel } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { isCommunityMember, type Person } from "@/react/types/AudienceList";

interface ListViewCardHeaderProps {
  canReceiveDmFromCurrentMember?: boolean;
  communityMember: Person;
  shouldShowHiddenMemberTooltip?: boolean;
}

export const ListViewCardHeader = ({
  communityMember,
  shouldShowHiddenMemberTooltip = false,
  canReceiveDmFromCurrentMember,
}: ListViewCardHeaderProps) => {
  const shouldShowHiddenMemberIcon =
    shouldShowHiddenMemberTooltip &&
    isCommunityMember(communityMember) &&
    !communityMember.visible_in_member_directory;

  const { isActive: isConnected, connection } = useConnectionButtonState(
    isCommunityMember(communityMember) ? communityMember : undefined,
  );

  const isConnectEnabled = useIsConnectEnabled();
  const isConnectPopoverPanel = useIsConnectPopoverPanel();
  const { isAvailable: isLocationVisible } = useIsMemberDirectoryMapAvailable();

  if (!communityMember) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-start justify-between gap-y-4 flex-row md:gap-y-0">
      <div className="flex min-w-0 flex-1 flex-col gap-y-0 md:gap-y-1">
        <header className="flex items-center gap-x-2" data-testid="member-name">
          <Typography.LabelSm weight="semibold">
            {communityMember.name}
          </Typography.LabelSm>
          {shouldShowHiddenMemberIcon && (
            <div
              aria-label={t(
                "members_directory.community_members.hidden_profile",
              )}
            >
              <TippyV2
                placement="right"
                content={t(
                  "members_directory.community_members.hidden_profile",
                )}
              >
                <Icon type="eye-closed-solid" size={16} />
              </TippyV2>
            </div>
          )}
        </header>
        <div className="text-default hidden min-w-0 text-left md:flex">
          <Typography.LabelXs truncate>
            {communityMember.headline}
          </Typography.LabelXs>
        </div>

        {isCommunityMember(communityMember) && (
          <div className="flex items-center gap-4 flex-wrap">
            {communityMember.location && isLocationVisible && (
              <div className="hidden items-center gap-1 md:flex">
                <Typography.LabelXs truncate color="text-default">
                  <Icon type="16-location" size={16} />
                  {communityMember.location}
                </Typography.LabelXs>
              </div>
            )}
            {!!communityMember.mutual_connections_count && (
              <div className="hidden items-center gap-2 md:flex">
                <Typography.LabelXs truncate color="text-default">
                  <Icon type="16-members" size={16} />
                  {t("members_directory.mutual_connections_count", {
                    count: communityMember.mutual_connections_count,
                  })}
                </Typography.LabelXs>
              </div>
            )}
          </div>
        )}
      </div>

      {!isConnectPopoverPanel && isCommunityMember(communityMember) && (
        <div className="flex items-end gap-x-2 gap-y-1 md:items-center">
          {isConnected && isConnectEnabled && (
            <ConnectedBadge
              connectedAt={connection?.accepted_at ?? connection?.created_at}
              showTooltip
              size="md"
            />
          )}
          <ConnectionButtonsOrchestrator
            member={communityMember}
            showMomentumModal={false}
            iconOnly={isConnected}
            canReceiveDmFromCurrentMember={canReceiveDmFromCurrentMember}
          />
        </div>
      )}
    </div>
  );
};
